import PageHero from "@/components/PageHero";
import SectionLabel from "@/components/SectionLabel";
import { ArrowUpRight, MapPin, Mail, Calendar } from "lucide-react";

const PATHS = [
  {
    tag: "Path 01",
    title: "Sentinel Diagnostic",
    desc: "A written diagnostic of your current blind spots, a measured trust-score gap and the potential for AML & fraud false-positive reduction — in two weeks.",
    cta: "Book a diagnostic",
    href: "/get-started?type=diagnostic",
    testId: "contact-cta-diagnostic",
  },
  {
    tag: "Path 02",
    title: "Workshop with your team",
    desc: "A one-day workshop with your compliance, fraud and engineering leads. We map your stack and your regulatory exposure.",
    cta: "Book a workshop",
    href: "/get-started?type=workshop",
    testId: "contact-cta-workshop",
  },
  {
    tag: "Path 03",
    title: "Tailored proposal",
    desc: "Already know what you need? Tell us your model, stack and footprint. We come back with a tailored proposal.",
    cta: "Request a proposal",
    href: "/get-started?type=proposal",
    testId: "contact-cta-proposal",
  },
];

export default function Contact() {
  return (
    <div data-testid="contact-page">
      <PageHero
        eyebrow="Contact"
        title="Two problems. One conversation."
        sub="Whether you want to see false-positive reduction on your own AML & fraud alerts, or the missed risk that hides today without entity resolution and cross-system context — start with a short, confidential conversation. Pick a door."
        breadcrumb={[{ label: "Lexifide.ai", to: "/" }, { label: "Contact" }]}
        testId="contact-hero"
      />

      <section className="light-section py-24 lg:py-32">
        <div className="max-w-[1380px] mx-auto px-6 lg:px-10">
          <SectionLabel index="01" label="Pick a path" />
          <h2 className="mt-6 mb-14 font-display font-bold text-[#090a0f] text-4xl sm:text-5xl lg:text-[56px] tracking-tight leading-[1.02] max-w-[20ch]">
            From a first email to a tailored proposal.
          </h2>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-px bg-[#e4e4e7] border border-[#e4e4e7]">
            {PATHS.map((p, i) => {
              const ACCENTS = [
                { hex: "#0047ff", soft: "rgba(0,71,255,0.08)" },
                { hex: "#7c3aed", soft: "rgba(124,58,237,0.10)" },
                { hex: "#f59e0b", soft: "rgba(245,158,11,0.12)" },
              ];
              const a = ACCENTS[i % ACCENTS.length];
              return (
                <div
                  key={p.title}
                  className="bg-white p-8 lg:p-10 flex flex-col gap-6 min-h-[420px] relative"
                  data-testid={`contact-path-${i}`}
                >
                  <span
                    className="absolute top-0 left-0 right-0 h-[3px]"
                    style={{ backgroundColor: a.hex }}
                  />
                  <div className="flex items-center justify-between">
                    <span
                      className="px-2 py-1 font-mono text-[10px] tracking-[0.22em] uppercase"
                      style={{ color: a.hex, backgroundColor: a.soft }}
                    >
                      {p.tag}
                    </span>
                    <span className="font-mono text-[10px] tracking-[0.22em] uppercase text-[#a1a1aa]">
                      0{i + 1} / 03
                    </span>
                  </div>
                  <p className="font-display font-bold text-[#090a0f] text-[28px] tracking-tight leading-tight">
                    {p.title}
                  </p>
                  <p className="text-[#52525b] text-[15px] leading-relaxed">
                    {p.desc}
                  </p>
                  <a
                    href={p.href}
                    data-testid={p.testId}
                    className="btn-primary-light mt-auto w-fit"
                  >
                    {p.cta} <ArrowUpRight className="w-4 h-4" />
                  </a>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Office */}
      <section className="dark-section py-24 lg:py-32 relative overflow-hidden">
        <div className="absolute inset-0 bg-dot-grid opacity-40" />
        <div className="relative max-w-[1380px] mx-auto px-6 lg:px-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center">
            <div className="lg:col-span-6">
              <SectionLabel index="02" label="Where we are" dark />
              <h2 className="mt-6 font-display font-bold text-white text-4xl sm:text-5xl lg:text-[52px] tracking-tight leading-[1.02]">
                Stockholm.<br />
                <span className="text-white/55">EU-resident, by design.</span>
              </h2>
              <p className="mt-6 text-white/65 text-[17px] leading-relaxed max-w-[52ch]">
                Sentinel data and infrastructure live in AWS Stockholm. Per-tenant
                KMS keys. DORA Article 30 provisions in our contracts. Exit
                strategy and incident reporting agreed on day one.
              </p>

              <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-px bg-white/10 border border-white/10">
                <div className="bg-[#0d0e16] p-6">
                  <div className="flex items-center gap-3 mb-3">
                    <MapPin className="w-4 h-4 text-[#00e5ff]" />
                    <p className="font-mono text-[10px] tracking-[0.22em] uppercase text-white/45">
                      Office
                    </p>
                  </div>
                  <p className="text-white text-[15px] leading-relaxed">
                    LXF Intelligence AB<br />
                    Stockholm, Sweden
                  </p>
                </div>
                <div className="bg-[#0d0e16] p-6">
                  <div className="flex items-center gap-3 mb-3">
                    <Mail className="w-4 h-4 text-[#00e5ff]" />
                    <p className="font-mono text-[10px] tracking-[0.22em] uppercase text-white/45">
                      Email
                    </p>
                  </div>
                  <a
                    href="mailto:hello@lexifide.ai"
                    data-testid="contact-email-link"
                    className="text-white text-[15px] link-underline"
                  >
                    hello@lexifide.ai
                  </a>
                </div>
                <div className="bg-[#0d0e16] p-6">
                  <div className="flex items-center gap-3 mb-3">
                    <Calendar className="w-4 h-4 text-[#00e5ff]" />
                    <p className="font-mono text-[10px] tracking-[0.22em] uppercase text-white/45">
                      Response time
                    </p>
                  </div>
                  <p className="text-white text-[15px] leading-relaxed">
                    Within one business day,<br />Mon&ndash;Fri, CET.
                  </p>
                </div>
                <div className="bg-[#0d0e16] p-6">
                  <div className="flex items-center gap-3 mb-3">
                    <span className="w-1.5 h-1.5 rounded-full bg-[#00e5ff] pulse-ring relative inline-block" />
                    <p className="font-mono text-[10px] tracking-[0.22em] uppercase text-white/45">
                      Region
                    </p>
                  </div>
                  <p className="text-white text-[15px] leading-relaxed">
                    AWS eu-north-1<br />Stockholm region
                  </p>
                </div>
              </div>
            </div>

            <div className="lg:col-span-6">
              <div className="aspect-[5/6] w-full overflow-hidden border border-white/10">
                <img
                  src="https://images.pexels.com/photos/37476837/pexels-photo-37476837.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=720"
                  alt="Stockholm office exterior"
                  loading="lazy"
                  className="w-full h-full object-cover grayscale contrast-110"
                  data-testid="contact-office-image"
                />
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
